/**
 * The Team page — v19 D1, THE DEV-HEAD VIEW. Route `team` (under `/countries/:cc`, but
 * the view is cross-country: `global: true` in the nav registry, and the data comes from
 * the bare `GET /team/workload`, scoped by the TOKEN — see `team-api.ts`).
 *
 * ===========================================================================
 * WHAT THE SEAT ASKED FOR, IN THE ORDER IT ASKED
 * ===========================================================================
 *   1. Workload per developer — assigned / in progress / in testing / returned (the
 *      brief's "blocked") / ready — as a thin stacked bar AND the counts beside it (the
 *      table is the chart's WCAG twin; the bar is the glance).
 *   2. What is STUCK — days-in-stage is the signal, so it is not buried: a STUCK stat
 *      tile leads the page, a STUCK column sits on every developer's row, and the chase
 *      list ("Stuck — longest in stage first") is its own card, ordered by staleness,
 *      each row linking to the work order in ITS country.
 *   3. Across ALL countries the token admits — `countries[]` is ECHOED in the subtitle,
 *      and a TZ-only seat sees a TZ-only page without any control having been moved.
 *   4. Money visible to the Dev Head and masked to a Developer ON THE SAME SCREEN: the
 *      per-country `in_flight[].amount` and each stuck row's `amount` arrive already
 *      decided by the API per `country_code`; this page renders the figure, the marker
 *      (`'•••'`, `text-fg-secondary` — the measured AA class, `team.contrast.test.ts`)
 *      or the em dash, and never sums across countries (proposal §8 #7 — the only
 *      cross-currency figure in the product lives behind the FX gate on the group view).
 *
 * ===========================================================================
 * WHAT IS DELIBERATELY NOT HERE
 * ===========================================================================
 * No ranking: developers render in the order the server sends (name), no rank column, no
 * sort-by-count. No "over capacity", no DUE dates, no job titles, no Tally/Cross-platform
 * tabs (Figma 07 draws them; no data exists — proposal §8 #21). No period control: this
 * is a NOW view. `in_progress` is shown even though the lifecycle never writes it today
 * (ESC-17) — a zero column is the honest report, not a hidden one.
 *
 * THE GATE IS `crf.view` — the route's own key — so a Developer reaches the page and reads
 * it masked, which is exactly the contrast the brief asked for (proposal §6 (b) records
 * how to hide it from Developers instead, if the owner prefers).
 */

import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { TriangleAlert } from 'lucide-react';

import { useCountryCode } from '../../auth/guards';
import { useSession } from '../../auth/session';
import { PageHeader } from '../../components/shell/AppShell';
import { Alert, Card, CountryBadge, EmptyState, SkeletonRows } from '../../components/ui';
import { cn } from '../../lib/cn';
import { formatDateLong, formatMoney, formatTime, isMaskedAmount } from '../inquiries/format';
import { BUCKET_LABELS, WORKLOAD_BUCKETS, fetchTeamWorkload, teamErrorText, teamKeys } from './team-api';
import type { DeveloperWorkloadDto, InFlightMoneyDto, StuckBucket, StuckItemDto, TeamWorkloadDto } from './team-api';
import { TeamWorkloadBar, UnassignedBar, WorkloadLegend } from './TeamWorkloadBar';
import { BUCKET_FILL, UNASSIGNED_FILL } from './team-workload-palette';

export function TeamWorkloadPage(): React.JSX.Element {
  const countryCode = useCountryCode();
  const { can } = useSession();
  // The ROUTE's key. A Developer holds it and is meant to reach this page — masked.
  const canView = can(countryCode, 'crf.view');

  const query = useQuery({
    queryKey: teamKeys.workload(),
    queryFn: ({ signal }) => fetchTeamWorkload(signal),
    enabled: canView,
  });

  if (!canView) {
    return (
      <div className="flex flex-col gap-4 p-6">
        <Alert tone="danger" title="The Team view is not available to this account">
          Seeing the team's workload needs the CRF permission. Ask an administrator to widen
          your access.
        </Alert>
      </div>
    );
  }

  const data = query.data;

  return (
    <div className="flex flex-col gap-4 p-6">
      <PageHeader
        title="Team"
        subtitle={
          data === undefined
            ? undefined
            : `${String(data.developers.length)} ${data.developers.length === 1 ? 'developer' : 'developers'} with open work · ${data.countries.join(', ')}`
        }
      />

      {query.isPending ? (
        <div className="rounded-md border border-line bg-surface px-4">
          <SkeletonRows rows={6} label="Loading the team workload" />
        </div>
      ) : query.isError ? (
        <Alert tone="danger" title="The team workload could not be loaded">
          <p>{teamErrorText(query.error)}</p>
          <button
            type="button"
            onClick={() => {
              void query.refetch();
            }}
            className="mt-1 font-medium underline underline-offset-2"
          >
            Try again.
          </button>
        </Alert>
      ) : (
        <TeamWorkloadBody data={query.data} />
      )}
    </div>
  );
}

/* ──────────────────────────────────────────────────────────────────── body ── */

function TeamWorkloadBody({ data }: { data: TeamWorkloadDto }): React.JSX.Element {
  const openWork = useMemo(
    () => data.developers.reduce((sum, dev) => sum + dev.buckets.total, 0) + data.unassigned.total,
    [data],
  );
  const max = useMemo(
    () => Math.max(data.unassigned.total, ...data.developers.map((dev) => dev.buckets.total), 0),
    [data],
  );
  const stuck = data.stuck_items.length;
  const nothingInFlight = data.developers.length === 0 && data.unassigned.total === 0;

  return (
    <>
      {/* The three numbers the seat reads first. STUCK is the signal and leads by weight. */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <StatTile label="Open work" value={openWork} note={`${String(data.developers.length)} ${data.developers.length === 1 ? 'developer' : 'developers'} · ${String(data.countries.length)} ${data.countries.length === 1 ? 'country' : 'countries'}`} />
        <StatTile
          label="Unassigned"
          value={data.unassigned.total}
          note={
            data.unassigned.stuck > 0
              ? `${String(data.unassigned.stuck)} waiting ${String(data.stuck_after_days)}+ days`
              : 'nothing waiting long'
          }
        />
        <StatTile
          label="Stuck"
          value={stuck}
          signal={stuck > 0}
          note={`${String(data.stuck_after_days)}+ days in the same stage`}
        />
      </div>

      {nothingInFlight ? (
        <div className="rounded-md border border-dashed border-line bg-surface">
          <EmptyState
            title="No work in flight"
            description={`Nothing is open for a developer across ${data.countries.join(', ')}.`}
          />
        </div>
      ) : (
        <Card
          title="Workload by developer"
          action={<WorkloadLegend />}
          className="gap-4"
        >
          <WorkloadTable data={data} max={max} />
          <p className="text-label text-fg-secondary">
            Listed by name. Bars share one scale — the longest is {String(max)} open{' '}
            {max === 1 ? 'work order' : 'work orders'}. Returned = testing failed, back with the
            developer (the board's Correction column).
          </p>
        </Card>
      )}

      <Card
        title={
          <h2 className="flex items-center gap-2 text-h3 text-fg">
            Stuck
            <span className="text-body13 font-normal text-fg-secondary">longest in stage first</span>
          </h2>
        }
        className="gap-4"
      >
        {stuck === 0 ? (
          <EmptyState
            title="Nothing is stuck"
            description={`Every open work order moved within the last ${String(data.stuck_after_days)} days.`}
            className="py-6"
          />
        ) : (
          <StuckTable items={data.stuck_items} />
        )}
        <p className="text-label text-fg-secondary">
          Days in stage count from the last status event on the work order (or from its
          creation where no event exists — marked). The clock can reset on a non-status update,
          so the figure is a floor, never an overstatement.
        </p>
      </Card>

      <p className="text-label text-fg" data-testid="generated-at">
        Data as of {formatDateLong(data.generated_at)}, {formatTime(data.generated_at)}.
      </p>
    </>
  );
}

/* ─────────────────────────────────────────────────────────────── stat tile ── */

function StatTile({
  label,
  value,
  note,
  signal = false,
}: {
  label: string;
  value: number;
  note: string;
  /**
   * The STUCK tile lights up — the status-danger PAIR (tint + text, the days-pill recipe)
   * with an icon AND the label, never colour alone. The pair, not bare danger text on the
   * surface: `text-danger-fg` on `bg-surface` MEASURES 2.98:1 in dark mode (`#ca2525` is
   * mode-invariant, the surface is not) — below even the 3:1 large-text floor — while on
   * its own tint it is 4.52:1 in both modes. `team.contrast.test.ts` pins both numbers.
   */
  signal?: boolean;
}): React.JSX.Element {
  return (
    <section
      aria-label={`${label}: ${String(value)}`}
      className="flex flex-col gap-1 rounded-md border border-line bg-surface p-5"
    >
      <p className="text-label text-fg-secondary">{label}</p>
      <p className="flex items-center text-kpi">
        <span
          className={cn(
            'inline-flex items-center gap-2',
            signal ? 'rounded-md bg-danger-bg px-2.5 text-danger-fg' : 'text-fg',
          )}
        >
          {signal ? <TriangleAlert aria-hidden="true" className="size-5" /> : null}
          {value}
        </span>
      </p>
      <p className="text-body13 text-fg-secondary">{note}</p>
    </section>
  );
}

/* ────────────────────────────────────────────────────────── workload table ── */

const WORKLOAD_HEADERS = ['DEVELOPER', 'WORKLOAD'] as const;

function WorkloadTable({ data, max }: { data: TeamWorkloadDto; max: number }): React.JSX.Element {
  return (
    <div className="overflow-x-auto rounded-md border border-line bg-surface">
      <table className="w-full min-w-[1180px] border-collapse text-left">
        <caption className="sr-only">
          Open work per developer across {data.countries.join(', ')}, listed by name
        </caption>
        <colgroup>
          <col className="w-[200px]" />
          <col className="w-[220px]" />
          {WORKLOAD_BUCKETS.map((bucket) => (
            <col key={bucket} className="w-[104px]" />
          ))}
          <col className="w-[72px]" />
          <col className="w-[72px]" />
          <col className="w-[140px]" />
          <col />
        </colgroup>
        <thead>
          <tr className="h-10 border-b border-line bg-subtle">
            {WORKLOAD_HEADERS.map((header) => (
              <th key={header} scope="col" className="whitespace-nowrap px-3 text-th uppercase text-fg">
                {header}
              </th>
            ))}
            {WORKLOAD_BUCKETS.map((bucket) => (
              <th key={bucket} scope="col" className="whitespace-nowrap px-3 text-right text-th uppercase text-fg">
                {BUCKET_LABELS[bucket]}
              </th>
            ))}
            <th scope="col" className="whitespace-nowrap px-3 text-right text-th uppercase text-fg">
              Total
            </th>
            <th scope="col" className="whitespace-nowrap px-3 text-right text-th uppercase text-fg">
              Stuck
            </th>
            <th scope="col" className="whitespace-nowrap px-3 text-th uppercase text-fg">
              Countries
            </th>
            <th scope="col" className="whitespace-nowrap px-3 text-th uppercase text-fg">
              In flight
            </th>
          </tr>
        </thead>
        <tbody>
          {/* The Dev Head's own desk first — Figma 07 draws the Unassigned row, and waiting for
              an assignee is the stuckest state there is. */}
          <tr className="crf-register-row border-b border-line hover:bg-subtle" data-row="unassigned">
            <td className="px-3 align-middle">
              <span className="text-body font-medium text-fg">Unassigned</span>
              <span className="block text-label text-fg-secondary">waiting for a developer</span>
            </td>
            <td className="px-3 align-middle">
              <UnassignedBar total={data.unassigned.total} max={max} />
            </td>
            {WORKLOAD_BUCKETS.map((bucket) => (
              <td key={bucket} className="tnum px-3 text-right align-middle text-body text-fg-secondary">
                —
              </td>
            ))}
            <td className="tnum px-3 text-right align-middle text-body text-fg">{data.unassigned.total}</td>
            <td className="px-3 text-right align-middle">
              <StuckCount count={data.unassigned.stuck} />
            </td>
            <td className="px-3 align-middle">
              <CountryCounts rows={data.unassigned.by_country} />
            </td>
            <td className="px-3 align-middle text-body13 text-fg-secondary">—</td>
          </tr>
          {data.developers.map((dev) => (
            <DeveloperRow key={dev.user_id} dev={dev} max={max} />
          ))}
        </tbody>
      </table>
    </div>
  );
}

function DeveloperRow({ dev, max }: { dev: DeveloperWorkloadDto; max: number }): React.JSX.Element {
  return (
    <tr className="crf-register-row border-b border-line last:border-b-0 hover:bg-subtle" data-row={dev.user_id}>
      <td className="px-3 align-middle">
        <span className="text-body font-medium text-fg">{dev.name}</span>
      </td>
      <td className="px-3 align-middle">
        <TeamWorkloadBar buckets={dev.buckets} max={max} label={dev.name} />
      </td>
      {WORKLOAD_BUCKETS.map((bucket) => (
        <td
          key={bucket}
          className={cn(
            'tnum px-3 text-right align-middle text-body',
            dev.buckets[bucket] === 0 ? 'text-fg-secondary' : 'text-fg',
          )}
        >
          {dev.buckets[bucket]}
        </td>
      ))}
      <td className="tnum px-3 text-right align-middle text-body font-medium text-fg">{dev.buckets.total}</td>
      <td className="px-3 text-right align-middle">
        <StuckCount count={dev.stuck} />
      </td>
      <td className="px-3 align-middle">
        <CountryCounts rows={dev.by_country} />
      </td>
      <td className="px-3 align-middle">
        <InFlightCell rows={dev.in_flight} />
      </td>
    </tr>
  );
}

/** `0` is quiet; anything else is the signal — status pair + the number, never colour alone. */
function StuckCount({ count }: { count: number }): React.JSX.Element {
  if (count === 0) {
    return <span className="tnum text-body text-fg-secondary">0</span>;
  }
  return (
    <span
      className="tnum inline-flex items-center rounded-pill bg-danger-bg px-2 py-0.5 text-label font-medium text-danger-fg"
      title={`${String(count)} stuck`}
    >
      {count}
    </span>
  );
}

function CountryCounts({ rows }: { rows: readonly { country_code: TeamWorkloadDto['countries'][number]; count: number }[] }): React.JSX.Element {
  if (rows.length === 0) return <span className="text-body13 text-fg-secondary">—</span>;
  return (
    <span className="flex flex-wrap items-center gap-x-2 gap-y-1">
      {rows.map((row) => (
        <span key={row.country_code} className="inline-flex items-center gap-1">
          <CountryBadge code={row.country_code} />
          <span className="tnum text-body13 text-fg">{row.count}</span>
        </span>
      ))}
    </span>
  );
}

/**
 * THE MONEY CELL — one line per (country, currency), each decided by the API for THAT
 * country. Three sentences, never collapsed: a figure, `•••` (withheld from this seat here),
 * an em dash (nobody priced it). The marker wears `text-fg-secondary`, the class measured
 * AA on the surface in both modes (`masked-marker.contrast.test.tsx` history); the anchor
 * below is the same shape every other money cell in the app ships, so the sweep can find it.
 */
function InFlightCell({ rows }: { rows: readonly InFlightMoneyDto[] }): React.JSX.Element {
  if (rows.length === 0) return <span className="text-body13 text-fg-secondary">—</span>;
  return (
    <span className="flex flex-col gap-0.5">
      {rows.map((row) => (
        <span key={`${row.country_code}-${String(row.currency)}`} className="inline-flex items-center gap-2">
          <CountryBadge code={row.country_code} />
          <MoneyValue value={row.amount} currency={row.currency} />
        </span>
      ))}
    </span>
  );
}

function MoneyValue({ value, currency }: { value: string | null; currency: string | null }): React.JSX.Element {
  const masked = isMaskedAmount(value);
  return (
    <span
      className={cn('tnum text-body', masked ? 'text-fg-secondary' : 'text-fg')}
      title={masked ? 'Hidden — this account cannot view amounts in this country' : undefined}
    >
      {formatMoney(value, currency)}
    </span>
  );
}

/* ───────────────────────────────────────────────────────────── stuck table ── */

const STUCK_HEADERS = ['DAYS', 'CRF', 'CUSTOMER', 'COUNTRY', 'STAGE', 'ASSIGNED TO', 'SINCE', 'AMOUNT'] as const;

const BUCKET_PILL_LABEL: Readonly<Record<StuckBucket, string>> = {
  ...BUCKET_LABELS,
  unassigned: 'Unassigned',
};

function StuckTable({ items }: { items: readonly StuckItemDto[] }): React.JSX.Element {
  return (
    <div className="overflow-x-auto rounded-md border border-line bg-surface">
      <table className="w-full min-w-[1080px] border-collapse text-left">
        <caption className="sr-only">Open work orders stuck in their stage, longest first</caption>
        <colgroup>
          <col className="w-[84px]" />
          <col className="w-[190px]" />
          <col />
          <col className="w-[96px]" />
          <col className="w-[140px]" />
          <col className="w-[180px]" />
          <col className="w-[200px]" />
          <col className="w-[150px]" />
        </colgroup>
        <thead>
          <tr className="h-10 border-b border-line bg-subtle">
            {STUCK_HEADERS.map((header) => (
              <th
                key={header}
                scope="col"
                className={cn('px-3 text-th uppercase text-fg', header === 'AMOUNT' && 'text-right')}
              >
                {header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {items.map((item) => (
            <tr key={item.work_order_id} className="crf-register-row border-b border-line last:border-b-0 hover:bg-subtle">
              <td className="px-3 align-middle">
                <span
                  className="tnum inline-flex items-center rounded-pill bg-danger-bg px-2.5 py-1 text-label font-medium text-danger-fg"
                  title={`${String(item.days_in_stage)} days in this stage`}
                >
                  {item.days_in_stage}d
                </span>
              </td>
              <td className="px-3 align-middle">
                {/* The work order in ITS country — a Dev Head standing in IN reaches a TZ record. */}
                <Link
                  to={`/countries/${item.country_code}/work/${item.work_order_id}`}
                  className="font-mono text-mono text-fg underline-offset-2 hover:underline"
                >
                  {item.crf_doc_no ?? `WO ${item.work_order_id}`}
                </Link>
              </td>
              <td className="px-3 align-middle text-body text-fg">{item.party_name ?? '—'}</td>
              <td className="px-3 align-middle">
                <CountryBadge code={item.country_code} />
              </td>
              <td className="px-3 align-middle">
                <StagePill bucket={item.bucket} />
              </td>
              <td className="px-3 align-middle text-body text-fg">
                {item.assigned_to === null ? (
                  <span className="text-fg-secondary">nobody</span>
                ) : (
                  item.assigned_to.name
                )}
              </td>
              <td className="px-3 align-middle text-body13 text-fg">
                {formatDateLong(item.stage_since)}
                {item.stage_since_source === 'created_at' ? (
                  <span className="block text-label text-fg-secondary">from creation — no stage event</span>
                ) : null}
              </td>
              <td className="px-3 text-right align-middle">
                <MoneyValue value={item.amount} currency={item.currency} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** The bucket as a pill: a swatch of the bar's own colour beside the word, on the quiet pair. */
function StagePill({ bucket }: { bucket: StuckBucket }): React.JSX.Element {
  const fill = bucket === 'unassigned' ? UNASSIGNED_FILL : BUCKET_FILL[bucket];
  return (
    <span className="inline-flex items-center gap-1.5 rounded-pill bg-subtle px-2 py-0.5 text-label font-medium text-fg ring-1 ring-line">
      <span aria-hidden="true" className={cn('inline-block size-2 rounded-[2px]', fill)} />
      {BUCKET_PILL_LABEL[bucket]}
    </span>
  );
}
